import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { Search, ArrowClockwise } from "react-bootstrap-icons";
import { toast } from "react-toastify";
import api from "../../services/api";
import PaginationBarComp from "../Pagination/PaginationBar";
import PurchaseAccountItem from "./PurchaseAccountItem";

const initialFilters = {
  search: "",
  user: "",
  store: "",
  source: "",
  refundStatus: "",
  startDate: "",
  endDate: "",
};

const PurchaseAccountList = ({ role, title = "حساب خرید" }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(
        Object.entries({ ...appliedFilters, page, perPage, sortBy: "createdAt", sortOrder: "DESC" })
          .filter(([, value]) => value !== "" && value !== null && value !== undefined)
      );
      const { data } = await api.get("/wallet/purchase-accounts", { params });
      setItems(data.items || []);
      setTotal(Number(data.total || 0));
      setTotalPages(Number(data.totalPages || 1));
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطا در دریافت حساب خریدها");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page, perPage]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
    setPage(1);
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <h4 className="mb-1">{title}</h4>
          <div className="text-muted small">نمایش خریدها به همراه سوابق برگشت و شارژ اعتبار</div>
        </div>
        <div className="text-muted small">تعداد خریدها: <strong>{total.toLocaleString("fa-IR")}</strong></div>
      </div>

      <Card className="border-0 shadow-sm mb-3">
        <Card.Body className="p-3">
          <Form onSubmit={handleSubmit}>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={role === "ADMIN" ? 4 : 5}>
                <Form.Label className="small">جستجو</Form.Label>
                <Form.Control
                  size="sm"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="نام، موبایل، فروشگاه، مبلغ، شماره تراکنش..."
                />
              </Col>
              {role !== "USER" && (
                <Col xs={12} sm={6} md={role === "ADMIN" ? 2 : 3}>
                  <Form.Label className="small">کاربر</Form.Label>
                  <Form.Control size="sm" value={filters.user} onChange={(e) => setFilters({ ...filters, user: e.target.value })} placeholder="نام یا موبایل" />
                </Col>
              )}
              {role === "ADMIN" && (
                <Col xs={12} sm={6} md={2}>
                  <Form.Label className="small">فروشگاه</Form.Label>
                  <Form.Control size="sm" value={filters.store} onChange={(e) => setFilters({ ...filters, store: e.target.value })} placeholder="نام فروشگاه" />
                </Col>
              )}
              <Col xs={6} sm={4} md={role === "ADMIN" ? 2 : 2}>
                <Form.Label className="small">منبع خرید</Form.Label>
                <Form.Select size="sm" value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
                  <option value="">همه</option>
                  <option value="wallet">کیف پول</option>
                  <option value="credit">اعتبار</option>
                </Form.Select>
              </Col>
              <Col xs={6} sm={4} md={2}>
                <Form.Label className="small">وضعیت برگشت</Form.Label>
                <Form.Select size="sm" value={filters.refundStatus} onChange={(e) => setFilters({ ...filters, refundStatus: e.target.value })}>
                  <option value="">همه وضعیت‌ها</option>
                  <option value="none">بدون برگشت</option>
                  <option value="partial">برگشت جزئی</option>
                  <option value="full">برگشت کامل</option>
                </Form.Select>
              </Col>
              <Col xs={6} sm={4} md={2}>
                <Form.Label className="small">از تاریخ</Form.Label>
                <Form.Control size="sm" type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
              </Col>
              <Col xs={6} sm={4} md={2}>
                <Form.Label className="small">تا تاریخ</Form.Label>
                <Form.Control size="sm" type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
              </Col>
              <Col xs={12} md="auto" className="d-flex gap-2">
                <Button type="submit" size="sm" variant="primary" className="flex-grow-1">
                  <Search className="me-1" /> جستجو
                </Button>
                <Button type="button" size="sm" variant="outline-secondary" onClick={handleReset} title="پاک کردن فیلترها">
                  <ArrowClockwise />
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" /></div>
      ) : items.length === 0 ? (
        <Alert variant="light" className="text-center border">خریدی با شرایط انتخاب‌شده پیدا نشد.</Alert>
      ) : (
        <>
          {items.map((purchase) => <PurchaseAccountItem key={purchase.id} purchase={purchase} role={role} />)}
          <PaginationBarComp page={page} setPage={setPage} totalPages={totalPages} perPage={perPage} setPerPage={setPerPage} />
        </>
      )}
    </div>
  );
};

export default PurchaseAccountList;
